import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import useIdleLogout from "@/hooks/useIdleLogout";

const EmployerContext = createContext(null);
const KEY = "adelina_emp_token";

/**
 * Employer session context (Fase 3 cookie migration).
 *
 * Backend now sets `adelina_emp_token` as an httpOnly, Secure, SameSite=Lax
 * cookie on `POST /api/employer/login` and clears it on `POST /api/employer/logout`.
 * Cookies are attached automatically by axios (`withCredentials: true` in
 * `lib/api.js`), so we no longer need to manually forward the Authorization
 * header — but we still keep the token in localStorage during the transition
 * so any component that reads it (e.g. NotificationBell's session-presence
 * check, `useIdleLogout` gating) keeps working exactly like before.
 */
export function EmployerProvider({ children }) {
  const [employer, setEmployer] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/employer/me");
      setEmployer(data);
    } catch {
      localStorage.removeItem(KEY);
      setEmployer(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/employer/login", { email, password });
    if (data.token) localStorage.setItem(KEY, data.token);
    setEmployer(data.employer);
    return data.employer;
  }, []);

  const register = useCallback(async (payload) => {
    return (await api.post("/employer/register", payload)).data;
  }, []);

  const logout = useCallback(() => {
    // Fire-and-forget backend logout so the httpOnly cookie is cleared.
    api.post("/employer/logout").catch(() => null);
    localStorage.removeItem(KEY);
    setEmployer(null);
  }, []);

  // Auto-logout after 5 minutes of inactivity — matches admin session policy.
  useIdleLogout({
    enabled: !!employer,
    idleMinutes: 5,
    onLogout: logout,
    storageKey: "adelina_emp_last_activity",
  });

  const authHeader = useCallback(() => {
    const t = localStorage.getItem(KEY);
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const value = useMemo(
    () => ({ employer, loading, login, register, logout, authHeader, refresh: fetchMe }),
    [employer, loading, login, register, logout, authHeader, fetchMe]
  );

  return (
    <EmployerContext.Provider value={value}>
      {children}
    </EmployerContext.Provider>
  );
}

export const useEmployer = () => useContext(EmployerContext);
export const EMPLOYER_TOKEN_KEY = KEY;
